import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  PrimaryColor,
  TableBodyGrey,
  TableHeaderGrey,
  TableRowHover,
} from "../../shared/styles/colors";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import { Card, CardContent, Typography, Button } from "@material-ui/core";
import { TablePagination } from "@mui/material";
import { Spinner } from "../../components";
import { ToastContainer } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import AlertService from "../../service/AlertService";
import ApiService from "../../service/ApiService";
import ToastMessages from "../../model/enum/ToastMessages";
import UserModal from "./UserModal";
import User from "../../model/user";
import Role from "../../model/role";
import DateConverter from "../../utils/DateConverter";
import UserDetailModal from "./UserDetailModal";
import CachedIcon from "@mui/icons-material/Cached";
import PageResultResponse from "../../model/pageResultResponse";
import Pathnames from "../../model/enum/Pathnames";
import Misc from "../../utils/Misc";
import useStyles from "../styles/cardStyles";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: TableHeaderGrey,
    color: PrimaryColor,
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 0,
    position: "static",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: TableBodyGrey,
    fontFamily: "monospace",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: TableRowHover,
  },
}));

const Users = () => {
  const classes = useStyles();
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [roles, setRoles] = useState<Role[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  // Update the URL with the current page and rowsPerPage
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.searchParams.set("rowsPerPage", rowsPerPage.toString());
    window.history.pushState({}, Pathnames.APPOINTMENTS_LIST, url);
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        searchParams.set("page", page.toString());
        searchParams.set("rowsPerPage", rowsPerPage.toString());

        const [userData, roleData] = await Promise.all([
          ApiService.get<PageResultResponse>(
            `${DatabaseEntities.USERS}?${searchParams.toString()}`
          ),
          ApiService.get<PageResultResponse>(DatabaseEntities.ROLES),
        ]);
        if (Misc.isArrayEmpty(userData.items)) return;
        setUsers(userData.items);
        setTotalPages(userData.count);
        setRoles(roleData.items);
      } catch (error) {
        AlertService.error(ToastMessages.FETCH_FAILED);
      } finally {
        setLoading(false);
      }
    };

    if (Misc.isArrayEmpty(users)) fetchData();
  }, [users, roles]);

  const resetList = (): void => {
    setPage(1);
    setUsers([]);
    setLoading(true);
    if (searchParams.has("userId")) {
      searchParams.delete("userId");
      setSearchParams(searchParams);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setUsers([]);
    setRoles([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const addIdToQueryPath = (id?: number) => {
    if (!id) return;

    const url = new URL(window.location.href);
    url.searchParams.set("userId", id.toString());
    window.history.pushState({}, Pathnames.APPOINTMENTS_LIST, url);
  };

  const removeIdFromQueryPath = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("userId");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  return (
    <>
      <Card raised className={classes.card}>
        <div className={classes.headerContainer}>
          <Typography className={classes.title}>Users</Typography>
          <div className={classes.headerDiv}>
            <UserModal roles={roles} resetList={resetList} />
            <Button onClick={resetList}>
              <CachedIcon />
            </Button>
          </div>
        </div>
        <CardContent>
          <Paper>
            <TableContainer>
              <Table
                stickyHeader
                aria-label="customized table"
                padding="normal"
                size="medium"
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="right">Id</StyledTableCell>
                    <StyledTableCell align="right">Username</StyledTableCell>
                    <StyledTableCell align="right">Name</StyledTableCell>
                    <StyledTableCell align="right">Email</StyledTableCell>
                    <StyledTableCell align="right">Role</StyledTableCell>
                    <StyledTableCell align="right">Created At</StyledTableCell>
                    <StyledTableCell align="right">Updated At</StyledTableCell>
                    <StyledTableCell align="right">
                      <MoreVertIcon />
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                {loading ? (
                  <Spinner />
                ) : (
                  <TableBody>
                    {users.length > 0 ? (
                      users.map((row: User) => (
                        <StyledTableRow key={row.id}>
                          <StyledTableCell align="right">
                            {row.id}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.username}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.firstName} {row.lastName}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.email}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {row.Role?.name}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDateAndHours(row.createdAt)}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {DateConverter.toFullDateAndHours(row.updatedAt)}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <UserDetailModal
                              user={row}
                              roles={roles}
                              resetList={resetList}
                              onOpen={() => {
                                addIdToQueryPath(row?.id);
                              }}
                              onClose={() => {
                                removeIdFromQueryPath();
                              }}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : (
                      <StyledTableCell align="left">No items</StyledTableCell>
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Paper>
          <TablePagination
            component="div"
            count={totalPages}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
      <ToastContainer />
    </>
  );
};

export default Users;
